// features/workstations/dashboard-sections/position-sizing.js
// ═══════════════════════════════════════════════════════════
// POSITION SIZING — C7 (Tier C, Constrained Action Calculator)
// ═══════════════════════════════════════════════════════════
// Domain-agnostic action calculator. Given user's constraints
// + current state + the action they're considering, compute
// the safe / recommended quantity.
//
// Two modes auto-detected from workstation.config.criteria:
//
//   r_risk        — classic R-based sizing
//                   distance_per_share = entry − stop
//                   max_risk_$ = account × risk_pct
//                   shares = floor(max_risk_$ / distance_per_share)
//                   Used when stop_loss_method is set on the workstation
//                   (Day Trading, swing trading, futures).
//
//   percent_alloc — stage-based % of account allocation
//                   base = account × sizing_by_stage[stage]
//                   final = base × macro_mult × catalyst_mult × correl
//                   Used when sizing_by_stage exists on the workstation
//                   (Long-Term 10x).
//
// Account size + risk pct stored client-side (localStorage) keyed
// by workstation_id. This is the V1 — when we add a broader
// user-settings table they'll move there. Storing locally keeps
// the widget shippable without a migration.
//
// The widget is read-only on the workstation config — it never
// writes back to it. This is a calculator, not a config editor.
// ═══════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

const DEFAULT_R_RISK_PCT = 0.5;   // 0.5% account per trade
const DEFAULT_ACCOUNT_USD = 50000;

const MACRO_MULT_OPTIONS = [
  { key: 'risk_on',  label: 'risk-on',  mult: 1.0  },
  { key: 'neutral',  label: 'neutral',  mult: 0.7  },
  { key: 'caution',  label: 'caution',  mult: 0.4  },
  { key: 'protect',  label: 'protect',  mult: 0.0  },
];

const CATALYST_MULT_OPTIONS = [
  { key: 'none',    label: 'no catalyst',     mult: 0.8 },
  { key: 'one',     label: '1 catalyst',      mult: 1.0 },
  { key: 'two',     label: '2 catalysts',     mult: 1.15 },
  { key: 'three',   label: '3+ catalysts',    mult: 1.3 },
];

const CORRELATION_PENALTY_OPTIONS = [
  { key: 'low',     label: 'low correlation',     penalty: 1.0  },
  { key: 'med',     label: 'moderate correlation', penalty: 0.85 },
  { key: 'high',    label: 'high correlation',    penalty: 0.7  },
];

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function PositionSizingSection({ workstation }) {
  const config = workstation?.config || {};
  const criteria = config.criteria || {};
  const identity = config.identity || {};

  // Mode detection
  const sizingByStage = criteria.sizing_by_stage || null;
  const hasStageMode = sizingByStage && Object.keys(sizingByStage).length > 0;
  const stopLossMethod = criteria.stop_loss_method || null;
  const hasRRiskMode = !!stopLossMethod || identity.session_cadence === 'daily' || identity.session_cadence === 'intraday';

  // Default mode preference: stage-based wins when both exist (long-term workspace)
  const defaultMode = hasStageMode ? 'percent_alloc' : 'r_risk';
  const [mode, setMode] = useState(defaultMode);

  // Persisted account + risk settings (localStorage, scoped per workstation)
  const settingsKey = `position-sizing:${workstation?.id || 'default'}`;
  const [accountUsd, setAccountUsd] = useState(DEFAULT_ACCOUNT_USD);
  const [maxRiskPct, setMaxRiskPct] = useState(DEFAULT_R_RISK_PCT);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(settingsKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed.accountUsd === 'number') setAccountUsd(parsed.accountUsd);
        if (typeof parsed.maxRiskPct === 'number') setMaxRiskPct(parsed.maxRiskPct);
      }
    } catch (_e) { /* ignore */ }
  }, [settingsKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(settingsKey, JSON.stringify({ accountUsd, maxRiskPct }));
    } catch (_e) { /* ignore */ }
  }, [settingsKey, accountUsd, maxRiskPct]);

  // ─── Render ───
  return (
    <section className="ws-sizing">
      <Eyebrow parts={[
        'position sizing',
        mode === 'percent_alloc' ? 'stage-based' : 'R-based',
        accountUsd ? `${fmtUsd(accountUsd)} account` : null,
      ]} />

      <SerifHeadline>{headlineFor(mode)}</SerifHeadline>
      <SerifSummary>{summaryFor(mode)}</SerifSummary>

      {/* Mode toggle (only shown if both modes are sensible) */}
      {hasStageMode && hasRRiskMode && (
        <div className="ws-sizing-mode-row">
          <button
            type="button"
            className={`ws-sizing-mode ${mode === 'percent_alloc' ? 'is-active' : ''}`}
            onClick={() => setMode('percent_alloc')}
          >
            stage-based (long-term)
          </button>
          <button
            type="button"
            className={`ws-sizing-mode ${mode === 'r_risk' ? 'is-active' : ''}`}
            onClick={() => setMode('r_risk')}
          >
            R-based (intraday)
          </button>
        </div>
      )}

      {/* Account + risk settings (always visible, both modes use them) */}
      <SectionStrip label="account settings (saved locally)">
        <div className="ws-sizing-settings-row">
          <label className="ws-sizing-setting">
            <span className="ws-sizing-setting-label">account size</span>
            <div className="ws-sizing-setting-input-group">
              <span className="ws-sizing-setting-prefix">$</span>
              <input
                type="number"
                className="ws-sizing-setting-input"
                value={accountUsd}
                step={1000}
                min={0}
                onChange={(e) => setAccountUsd(Math.max(0, Number(e.target.value) || 0))}
              />
            </div>
          </label>
          {mode === 'r_risk' && (
            <label className="ws-sizing-setting">
              <span className="ws-sizing-setting-label">max risk per trade</span>
              <div className="ws-sizing-setting-input-group">
                <input
                  type="number"
                  className="ws-sizing-setting-input ws-sizing-setting-input-narrow"
                  value={maxRiskPct}
                  step={0.05}
                  min={0.05}
                  max={5}
                  onChange={(e) => setMaxRiskPct(Math.max(0.05, Math.min(5, Number(e.target.value) || 0.5)))}
                />
                <span className="ws-sizing-setting-suffix">% of account</span>
              </div>
            </label>
          )}
        </div>
      </SectionStrip>

      {/* Calculator body */}
      {mode === 'r_risk' && (
        <RRiskCalculator accountUsd={accountUsd} maxRiskPct={maxRiskPct} />
      )}
      {mode === 'percent_alloc' && (
        <PercentAllocCalculator
          accountUsd={accountUsd}
          sizingByStage={sizingByStage}
          criteria={criteria}
        />
      )}

      <PractitionerLens label="how to use this">
        Calculator only — never an order. Plug in the trade you’re
        considering; the widget shows the size your stated rules
        permit. If the recommendation feels too small, that’s the
        rules talking. If it feels too big, you’re missing a
        constraint. Either way, sizing decisions stay yours.
      </PractitionerLens>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// R-RISK CALCULATOR (Day Trading / R-based sizing)
// ═══════════════════════════════════════════════════════════
function RRiskCalculator({ accountUsd, maxRiskPct }) {
  const [ticker, setTicker] = useState('');
  const [entry, setEntry] = useState('');
  const [stop, setStop] = useState('');

  const result = useMemo(() => {
    const entryNum = Number(entry);
    const stopNum = Number(stop);
    if (!entryNum || !stopNum || entryNum <= 0 || stopNum <= 0) return null;
    if (entryNum === stopNum) return { error: 'Entry and stop must differ.' };
    const distance = Math.abs(entryNum - stopNum);
    const distancePct = (distance / entryNum) * 100;
    const maxRiskUsd = accountUsd * (maxRiskPct / 100);
    const shares = Math.floor(maxRiskUsd / distance);
    const positionUsd = shares * entryNum;
    const positionPct = accountUsd > 0 ? (positionUsd / accountUsd) * 100 : 0;
    const realizedRiskUsd = shares * distance;

    const warnings = [];
    if (distancePct < 0.3) warnings.push({ level: 'amber', text: `Stop is very tight (${distancePct.toFixed(2)}% of price). Easy to get stopped on noise.` });
    if (distancePct > 3) warnings.push({ level: 'amber', text: `Stop is wide (${distancePct.toFixed(1)}% of price). Reconsider entry timing or stop placement.` });
    if (positionPct > 20) warnings.push({ level: 'terra', text: `Position would be ${positionPct.toFixed(1)}% of account. Concentration risk high.` });
    if (positionPct > 50) warnings.push({ level: 'terra', text: `Position exceeds 50% of account. Margin / liquidity risk on exit.` });
    if (shares <= 0) warnings.push({ level: 'terra', text: 'Distance to stop is too wide for the risk budget. Reduce risk pct or move stop.' });

    return {
      distance,
      distancePct,
      maxRiskUsd,
      shares,
      positionUsd,
      positionPct,
      realizedRiskUsd,
      warnings,
    };
  }, [entry, stop, accountUsd, maxRiskPct]);

  return (
    <SectionStrip label="trade inputs">
      <div className="ws-sizing-inputs">
        <label className="ws-sizing-input">
          <span className="ws-sizing-input-label">ticker (optional)</span>
          <input
            type="text"
            className="ws-sizing-input-field ws-sizing-input-ticker"
            value={ticker}
            placeholder="QQQ"
            maxLength={16}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
          />
        </label>
        <label className="ws-sizing-input">
          <span className="ws-sizing-input-label">entry price</span>
          <input
            type="number"
            className="ws-sizing-input-field"
            value={entry}
            step={0.01}
            min={0}
            placeholder="0.00"
            onChange={(e) => setEntry(e.target.value)}
          />
        </label>
        <label className="ws-sizing-input">
          <span className="ws-sizing-input-label">stop price</span>
          <input
            type="number"
            className="ws-sizing-input-field"
            value={stop}
            step={0.01}
            min={0}
            placeholder="0.00"
            onChange={(e) => setStop(e.target.value)}
          />
        </label>
      </div>

      {result?.error && (
        <div className="ws-sizing-error">{result.error}</div>
      )}

      {result && !result.error && (
        <div className="ws-sizing-result">
          <div className="ws-sizing-result-headline">
            <span className="ws-sizing-result-shares">{fmtInt(result.shares)}</span>
            <span className="ws-sizing-result-shares-label">shares{ticker ? ` of ${ticker}` : ''}</span>
          </div>
          <div className="ws-sizing-result-grid">
            <MetaField label="position value">{fmtUsd(result.positionUsd)} ({result.positionPct.toFixed(1)}% of account)</MetaField>
            <MetaField label="risk per share">${result.distance.toFixed(2)} ({result.distancePct.toFixed(2)}% of price)</MetaField>
            <MetaField label="max loss at stop">{fmtUsd(result.realizedRiskUsd)} (vs {fmtUsd(result.maxRiskUsd)} budget)</MetaField>
          </div>
          {result.warnings.length > 0 && (
            <div className="ws-sizing-warnings">
              {result.warnings.map((w, i) => (
                <div key={i} className={`ws-sizing-warning ws-sizing-warning-${w.level}`}>
                  {w.text}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </SectionStrip>
  );
}

// ═══════════════════════════════════════════════════════════
// PERCENT-ALLOC CALCULATOR (Long-Term / stage-based sizing)
// ═══════════════════════════════════════════════════════════
function PercentAllocCalculator({ accountUsd, sizingByStage, criteria }) {
  const stages = Object.keys(sizingByStage || {});
  const [stage, setStage] = useState(stages[0] || 'pilot');
  const [ticker, setTicker] = useState('');
  const [entry, setEntry] = useState('');
  const [macro, setMacro] = useState('neutral');
  const [catalyst, setCatalyst] = useState('one');
  const [correlation, setCorrelation] = useState('low');

  const result = useMemo(() => {
    if (!sizingByStage || !sizingByStage[stage]) return null;
    const entryNum = Number(entry);
    const basePct = sizingByStage[stage];
    const baseUsd = accountUsd * (basePct / 100);

    const macroOpt = MACRO_MULT_OPTIONS.find(o => o.key === macro) || MACRO_MULT_OPTIONS[1];
    const catalystOpt = CATALYST_MULT_OPTIONS.find(o => o.key === catalyst) || CATALYST_MULT_OPTIONS[1];
    const correlOpt = CORRELATION_PENALTY_OPTIONS.find(o => o.key === correlation) || CORRELATION_PENALTY_OPTIONS[0];

    const finalUsd = baseUsd * macroOpt.mult * catalystOpt.mult * correlOpt.penalty;
    const finalPct = accountUsd > 0 ? (finalUsd / accountUsd) * 100 : 0;
    const shares = entryNum > 0 ? Math.floor(finalUsd / entryNum) : null;

    const warnings = [];
    if (macroOpt.key === 'protect') {
      warnings.push({ level: 'terra', text: 'Macro is protect — your hard rules likely block any new positions. Calculator is showing 0.' });
    }
    if (finalPct > basePct * 1.4) {
      warnings.push({ level: 'amber', text: `Multipliers stacked above ${(basePct * 1.4).toFixed(1)}% — verify catalyst quality before sizing this aggressively.` });
    }
    // Macro gate hard rule from Long-Term seed
    const macroGateRule = (criteria.hard_rules || []).find(r => r.id === 'macro_gate');
    if (macroGateRule && (macroOpt.key === 'caution' || macroOpt.key === 'protect')) {
      warnings.push({ level: 'amber', text: `Hard rule (${macroGateRule.id}): ${macroGateRule.text}` });
    }

    return {
      stage,
      basePct,
      baseUsd,
      macroMult: macroOpt.mult,
      catalystMult: catalystOpt.mult,
      correlPenalty: correlOpt.penalty,
      finalUsd,
      finalPct,
      shares,
      formula: `${fmtUsd(baseUsd)} × ${macroOpt.mult} (macro) × ${catalystOpt.mult} (catalyst) × ${correlOpt.penalty} (correlation)`,
      warnings,
    };
  }, [stage, entry, accountUsd, sizingByStage, macro, catalyst, correlation, criteria]);

  return (
    <SectionStrip label="position inputs">
      <div className="ws-sizing-inputs">
        <label className="ws-sizing-input">
          <span className="ws-sizing-input-label">ticker (optional)</span>
          <input
            type="text"
            className="ws-sizing-input-field ws-sizing-input-ticker"
            value={ticker}
            placeholder="ABC"
            maxLength={16}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
          />
        </label>
        <label className="ws-sizing-input">
          <span className="ws-sizing-input-label">stage</span>
          <select
            className="ws-sizing-input-field"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
          >
            {stages.map(s => (
              <option key={s} value={s}>
                {s} ({sizingByStage[s]}%)
              </option>
            ))}
          </select>
        </label>
        <label className="ws-sizing-input">
          <span className="ws-sizing-input-label">entry price (for shares)</span>
          <input
            type="number"
            className="ws-sizing-input-field"
            value={entry}
            step={0.01}
            min={0}
            placeholder="0.00"
            onChange={(e) => setEntry(e.target.value)}
          />
        </label>
      </div>

      <div className="ws-sizing-multipliers">
        <Multiplier
          label="macro regime"
          options={MACRO_MULT_OPTIONS}
          value={macro}
          onChange={setMacro}
          showField="mult"
        />
        <Multiplier
          label="catalyst count"
          options={CATALYST_MULT_OPTIONS}
          value={catalyst}
          onChange={setCatalyst}
          showField="mult"
        />
        <Multiplier
          label="correlation w/ existing"
          options={CORRELATION_PENALTY_OPTIONS}
          value={correlation}
          onChange={setCorrelation}
          showField="penalty"
        />
      </div>

      {result && (
        <div className="ws-sizing-result">
          <div className="ws-sizing-result-headline">
            <span className="ws-sizing-result-shares">{fmtUsd(result.finalUsd)}</span>
            <span className="ws-sizing-result-shares-label">
              position value{ticker ? ` in ${ticker}` : ''}
              {result.shares != null && ticker ? ` · ~${fmtInt(result.shares)} shares` : ''}
            </span>
          </div>
          <div className="ws-sizing-result-grid">
            <MetaField label="stage base">{result.basePct}% = {fmtUsd(result.baseUsd)}</MetaField>
            <MetaField label="final allocation">{result.finalPct.toFixed(2)}% of account</MetaField>
            <MetaField label="formula">{result.formula}</MetaField>
          </div>
          {result.warnings.length > 0 && (
            <div className="ws-sizing-warnings">
              {result.warnings.map((w, i) => (
                <div key={i} className={`ws-sizing-warning ws-sizing-warning-${w.level}`}>
                  {w.text}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </SectionStrip>
  );
}

// ═══════════════════════════════════════════════════════════
// MULTIPLIER CONTROL
// ═══════════════════════════════════════════════════════════
function Multiplier({ label, options, value, onChange, showField }) {
  const current = options.find(o => o.key === value) || options[0];
  return (
    <div className="ws-sizing-mult">
      <span className="ws-sizing-mult-label">{label}</span>
      <div className="ws-sizing-mult-chips">
        {options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            className={`ws-sizing-mult-chip ${opt.key === value ? 'is-active' : ''}`}
            onClick={() => onChange(opt.key)}
            title={`${opt.label}: ×${opt[showField]}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <span className="ws-sizing-mult-value">×{current[showField]}</span>
    </div>
  );
}

// ─── Helpers ───
function headlineFor(mode) {
  if (mode === 'percent_alloc') return 'Stage × multipliers — what should this be sized at?';
  return 'Distance to stop × risk budget — how many shares?';
}

function summaryFor(mode) {
  if (mode === 'percent_alloc') {
    return 'Stage-based base allocation, adjusted by macro / catalyst / correlation. Calculator only — never an order.';
  }
  return 'R-based sizing from your entry, stop, and risk-per-trade rule. Calculator only — never an order.';
}

function fmtUsd(n) {
  if (n == null || Number.isNaN(n)) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function fmtInt(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US').format(Math.floor(n));
}
